import { z } from "zod";
import { zStoryFraming } from "./storyFraming";
import { zStoryProposal } from "./storyProposal";
import { zStoryReferenceImages } from "./storyOutline";
import { zWorldSceneStart } from "./worlds";
import { zImageReference } from "./imageReferenceSchema";
import type { DatabaseRecord } from "./schema";
import type { CreateResponse, CreateTargetEvent, ParsedCreateRequest } from "./create";
import type { ImageReference } from "./images";

const id = z.string().regex(/^[a-zA-Z0-9_-]{1,100}$/);
export const zStoryArtworkEdit = z
  .object({
    prompt: z.string().max(4000).default(""),
    images: zStoryReferenceImages.default([]),
    quality: z.enum(["fast", "best", "pro", "openai"]).default("fast"),
    requestId: id.optional(),
  })
  .strip();
export type StoryArtworkEdit = z.infer<typeof zStoryArtworkEdit>;

export const STORY_DRAFT_SETTINGS = {
  motion: "auto" as const,
  format: "auto" as const,
  length: "short" as const,
  participation: "ending" as const,
  fidelity: "faithful" as const,
  remember: true,
  framing: { mode: "responsive" as const, aspectRatio: 16 / 9 },
};
export const zStoryDraftState = z
  .object({
    idea: z.string().max(4000).default(""),
    worldId: z.string().max(100).default(""),
    stage: z.enum(["idea", "shape", "build", "preview"]).default("idea"),
    enhancedExtraction: z.boolean().optional(),
    worldStart: z
      .object({ timelineStart: zWorldSceneStart.optional(), anchorDescription: z.string().max(4000).optional() })
      .optional(),
    referenceImages: zStoryReferenceImages.optional(),
    proposal: zStoryProposal.optional(),
    history: z.array(zStoryProposal).max(10).default([]),
    settings: z
      .object({
        motion: z.enum(["auto", "still", "authored"]).default("auto"),
        format: z.enum(["auto", "spoken", "musical"]).default("auto"),
        length: z.enum(["short", "medium", "long"]).default("short"),
        participation: z.enum(["natural", "frequent", "ending"]).default("ending"),
        fidelity: z.enum(["faithful", "inspired"]).default("faithful"),
        remember: z.boolean().default(true),
        framing: zStoryFraming.default(STORY_DRAFT_SETTINGS.framing),
      })
      .default(STORY_DRAFT_SETTINGS),
    pieces: z
      .record(
        z.object({
          status: z.enum(["waiting", "working", "done", "failed"]),
          imageUrl: z.string().optional(),
          image: zImageReference.optional(),
          activity: z.string().optional(),
          targetId: id.optional(),
          error: z.string().optional(),
        })
      )
      .optional(),
    artworkEdits: z.record(zStoryArtworkEdit).optional(),
    storyId: id.optional(),
    sceneId: id.optional(),
    requestId: id.optional(),
    pending: z.object({ requestId: id, operation: z.enum(["propose", "realize", "scene"]) }).optional(),
    built: z.boolean().optional(),
    change: z.string().max(4000).optional(),
    reviseSceneId: id.optional(),
    playbackWarnings: z.array(z.string()).optional(),
  })
  .strip();
export type StoryDraftState = z.infer<typeof zStoryDraftState>;

/** Private authoring data, independent of playable Performance and world canon. */
export const zStoryDraft = z
  .object({
    schemaVersion: z.literal(1),
    id,
    accountId: id,
    status: z.enum(["active", "archived"]),
    revision: z.number().int().positive(),
    state: zStoryDraftState,
    createdBy: z.string().min(1),
    updatedBy: z.string().min(1),
  })
  .strip();
export type StoryDraft = z.infer<typeof zStoryDraft> & DatabaseRecord;

/** Immutable checkpoints; the revision matches the owning draft transaction. */
export interface StoryDraftRevision extends DatabaseRecord {
  revision: number;
  state: StoryDraftState;
  reason: string;
  actorId: string;
}

/** Server-owned generation receipts, separate from editable authoring state. */
export interface StoryDraftGeneration extends DatabaseRecord {
  request: ParsedCreateRequest;
  actorId: string;
  status: "running" | "complete" | "failed";
  startedAtMs: number;
  result?: CreateResponse;
  lookImage?: ImageReference;
  target?: CreateTargetEvent;
  error?: string | null;
  lastFailure?: { message: string; atMs: number };
}
